import { Injector } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";

import { MatomoTracker } from "ngx-matomo-client";

import { UmbracoIndstillinger } from "../umbraco-indstillinger/model/umbraco-indstillinger.model";
import { UmbracoIndstillingerObserver } from "../umbraco-indstillinger/umbraco-indstillinger.observer";

export const DEFAULT_MATOMO_SITE_ID = 2334;

/** Site ID allerede sat af provideMatomo — undgå gentagne setSiteId i _paq. */
let appliedMatomoSiteId: number | null = DEFAULT_MATOMO_SITE_ID;

/** Kun til unit tests. */
export function resetAppliedMatomoSiteIdForTests(siteId: number | null = DEFAULT_MATOMO_SITE_ID): void {
    appliedMatomoSiteId = siteId;
}

export function parseMatomoSiteId(matomoSiteId: string | undefined | null): number | null {
    if (!matomoSiteId) {
        return null;
    }
    const siteId = parseInt(matomoSiteId, 10);
    return Number.isFinite(siteId) && siteId > 0 ? siteId : null;
}

/**
 * Opdaterer Matomo site ID kun når værdien ændrer sig.
 * Gentagne setSiteId i _paq giver Matomo console.error-støj i Rapid7.
 */
export function applyMatomoSiteId(siteId: number, tracker?: MatomoTracker): void {
    if (appliedMatomoSiteId === siteId) {
        return;
    }
    appliedMatomoSiteId = siteId;
    if (tracker) {
        tracker.setSiteId(siteId);
        return;
    }
    const paq = (window as { _paq?: unknown[] })._paq;
    if (paq) {
        paq.push(["setSiteId", siteId]);
    }
}

function updateMatomoSiteIdFromUmbraco(settings: UmbracoIndstillinger | undefined, tracker: MatomoTracker): void {
    const siteId = parseMatomoSiteId(settings?.matomoSiteId) ?? DEFAULT_MATOMO_SITE_ID;
    applyMatomoSiteId(siteId, tracker);
}

function isPWAOrTWA(): boolean {
    return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as { standalone?: boolean }).standalone || document.referrer.includes("android-app://");
}

function trackPWAStatus(matomoTracker: MatomoTracker): void {
    const launchTracked = sessionStorage.getItem("matomo_launch_tracked");
    if (launchTracked) {
        return;
    }

    const isPWA = isPWAOrTWA();
    if (isPWA) {
        matomoTracker.trackEvent("Application", "App Launch", "PWA Launch");
    } else {
        matomoTracker.trackEvent("Application", "App Launch", "Web Launch");
    }

    sessionStorage.setItem("matomo_launch_tracked", "true");
}

function trackAppTypePageView(matomoTracker: MatomoTracker): void {
    const appType = isPWAOrTWA() ? "PWA" : "Browser";
    matomoTracker.trackEvent("Page View", "App Type", appType);
}

function initAppTypeTracking(injector: Injector): void {
    const router = injector.get(Router);
    const matomoTracker = injector.get(MatomoTracker);

    trackAppTypePageView(matomoTracker);

    router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        trackAppTypePageView(matomoTracker);
    });
}

/** Sætter Matomo site ID fra UmbracoIndstillinger (opdateres ved værkvalg / hydration). */
export function initMatomoFromUmbraco(injector: Injector): () => Promise<void> {
    return async () => {
        const umbracoIndstillingerObserver = injector.get(UmbracoIndstillingerObserver);
        const matomoTracker = injector.get(MatomoTracker);

        const initial = await umbracoIndstillingerObserver.getValue();
        updateMatomoSiteIdFromUmbraco(initial, matomoTracker);

        umbracoIndstillingerObserver.valueChanges.subscribe((settings) => {
            updateMatomoSiteIdFromUmbraco(settings, matomoTracker);
        });

        trackPWAStatus(matomoTracker);
        initAppTypeTracking(injector);
    };
}
